// @ts-strict-ignore
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { getPrevRealBlock } from 'cms/helpers';
import { MappedContentBlock } from 'cms/mappers';
import { Box } from 'components/Box';
import { Grid, GridItem } from 'components/Grid';
import { breakpoints, colors } from 'theme';

type Props = {
  id?: string;
  blocks: Array<MappedContentBlock>;
  block: MappedContentBlock;
  index: number;
  fullWidthLayout: boolean;
  showHeadingDivider?: boolean;
  hideFirstHeadingDivider?: boolean;
  withSidebar?: boolean;
  children: ReactNode;
};

export const ContentPageBlockContainer = styled(
  ({
    blocks,
    block,
    index,
    fullWidthLayout,
    showHeadingDivider,
    hideFirstHeadingDivider,
    withSidebar,
    children,
    ...props
  }: Props) => {
    const padding = fullWidthLayout
      ? [index === 0 ? 36 : 64, null, 70]
      : [40, null, 60];
    const prevBlock = getPrevRealBlock(blocks, index);
    return (
      <Box
        position="relative"
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        display={block.isHidden ? 'none' : block.isInline ? 'inline' : null}
        width="100%"
        mb={!block.padding || block.padding === 'DEFAULT' ? padding : 0}
        mt={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          withSidebar && index === 0
            ? [0, 0, 0]
            : !block.padding || block.padding === 'DEFAULT'
            ? padding
            : 0
        }
        py={block.hasBackground && padding}
        pt={
          index > 0 &&
          block.hasBackground &&
          prevBlock &&
          prevBlock.hasBackground
            ? [0, 0, 0]
            : null
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {withSidebar
          ? children
          : (() => {
              switch (block.width) {
                case 'NARROW':
                case 'INSET':
                case 'GRID':
                  if (fullWidthLayout || block.type === 'SPLIT_CONTENT') {
                    return (
                      <Grid>
                        <GridItem
                          columnStart={[1, 1, 1, 1]}
                          columnEnd={[13, 13, 13, 13]}
                        >
                          {children}
                        </GridItem>
                      </Grid>
                    );
                  }

                  return (
                    <Grid>
                      <GridItem
                        columnStart={[1, 1, 1, 2]}
                        columnEnd={[13, 13, 13, 12]}
                      >
                        {children}
                      </GridItem>
                    </Grid>
                  );

                case 'FULL':
                default:
                  return children;
              }
            })()}
      </Box>
    );
  }
)`
  ${(props) =>
    props.showHeadingDivider &&
    `
    h2${props.hideFirstHeadingDivider ? ':not(:first-child)' : ''} {
      width: 100%;
      &::before {
        content: '';
        display: block;
        margin: 0 auto;
        margin-bottom: ${props.fullWidthLayout ? 64 : 40}px;
        width: 100%;
        max-width: 1200px;
        height: 1px;
        background: ${colors.aluminum};
        @media (min-width: ${breakpoints[1]}) {
          margin-bottom: ${props.fullWidthLayout ? 70 : 60}px;
        }
      }
    }
  `}
  ${(props) =>
    props.block.hasBackground &&
    props.block.type !== 'SPLIT_CONTENT' &&
    `
    &::before {
      content: '';
      z-index: -1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${colors.fog};
      > * {
        position: relative;
      }
  }`}

  ${(props) =>
    props.block.hasBackground &&
    props.block.type === 'SPLIT_CONTENT' &&
    `

    @media (min-width: ${breakpoints[2]}) {

    &::before {
      content: '';
      z-index: -1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${colors.fog};
      > * {
        position: relative;
      }
  }}`}
`;
